/**
 * CART STORE
 * Store EventDriven pour la gestion du panier
 * Basé sur l'architecture du projet kingrecharge-store original
 */

import { EventDrivenStore } from '@core/store/eventDrivenStore';
import constants from '@core/utils/constants';

class CartStore extends EventDrivenStore {
  constructor() {
    super('cart', {
      items: [],
      deliveryLocation: null,
      promoCode: null,
      promoDiscount: 0,
      subtotal: 0,
      total: 0,
      loading: false,
    });
  }

  async setupEvents() {
    // Écouter l'événement app.ready
    this.on('app.ready', () => {
      this.loadCart();
    });

    // Écouter les événements d'ajout au panier
    this.on('cart:add', (data) => {
      const { product, quantity, options } = data;
      this.addItem(product, quantity, options);
    });

    // Écouter les événements de suppression du panier
    this.on('cart:remove', (itemId) => {
      this.removeItem(itemId);
    });

    // Écouter les événements de mise à jour de quantité
    this.on('cart:update:quantity', (data) => {
      const { itemId, quantity } = data;
      this.updateQuantity(itemId, quantity);
    });

    // Écouter les événements de vidage du panier
    this.on('cart:clear', () => {
      this.clearCart();
    });

    // Écouter les événements de définition du lieu de livraison
    this.on('cart:set:delivery', (location) => {
      this.setDeliveryLocation(location);
    });

    // Écouter les événements d'application de code promo
    this.on('cart:apply:promo', (code) => {
      this.applyPromoCode(code);
    });

    // Propager les événements du store vers le bus global
    this.on('cart:updated', (state) => {
      // Mettre à jour le compteur du panier dans l'UI
      window.$bus?.emit('cart:count:updated', this.getItemCount());
    });
  }

  /**
   * Ajoute un article au panier
   */
  addItem(product, quantity = 1, options = {}) {
    const existingItem = this.state.items.find(
      item => item.id === product.id && JSON.stringify(item.options) === JSON.stringify(options)
    );

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      this.state.items.push({
        id: product.id,
        name: product.name || product.title,
        title: product.title || product.name,
        price: product.price || product.amount || 0,
        quantity,
        options,
        image: product.image,
        category: product.category,
        type: product.type,
        raw: product,
      });
    }

    this.calculateTotals();
    this.saveCart();
    window.$bus?.emit('cart:updated', this.state);
  }

  /**
   * Supprime un article du panier
   */
  removeItem(itemId) {
    this.state.items = this.state.items.filter(item => item.id !== itemId);
    this.calculateTotals();
    this.saveCart();
    window.$bus?.emit('cart:updated', this.state);
  }

  /**
   * Met à jour la quantité d'un article
   */
  updateQuantity(itemId, quantity) {
    const item = this.state.items.find(item => item.id === itemId);
    if (item) {
      if (quantity <= 0) {
        this.removeItem(itemId);
      } else {
        item.quantity = quantity;
        this.calculateTotals();
        this.saveCart();
        window.$bus?.emit('cart:updated', this.state);
      }
    }
  }

  /**
   * Vide le panier
   */
  clearCart() {
    this.state.items = [];
    this.state.promoCode = null;
    this.state.promoDiscount = 0;
    this.calculateTotals();
    this.saveCart();
    window.$bus?.emit('cart:updated', this.state);
  }

  /**
   * Définit le lieu de livraison
   */
  setDeliveryLocation(location) {
    this.state.deliveryLocation = location;
    this.saveCart();
    window.$bus?.emit('cart:delivery:updated', this.state);
  }

  /**
   * Applique un code promo
   */
  applyPromoCode(code) {
    // TODO: Intégrer avec l'API pour vérifier le code promo
    this.state.promoCode = code;
    this.state.promoDiscount = 0; // Calculé après vérification API
    this.calculateTotals();
    this.saveCart();
    window.$bus?.emit('cart:promo:applied', { code, discount: this.state.promoDiscount });
  }

  /**
   * Calcule les totaux
   */
  calculateTotals() {
    this.state.subtotal = this.state.items.reduce(
      (sum, item) => sum + (item.price * item.quantity),
      0
    );
    this.state.total = Math.max(0, this.state.subtotal - this.state.promoDiscount);
  }

  /**
   * Compte le nombre d'articles
   */
  getItemCount() {
    return this.state.items.reduce((sum, item) => sum + item.quantity, 0);
  }

  /**
   * Sauvegarde le panier en localStorage
   */
  saveCart() {
    try {
      const userId = this.getUserId();
      const cartData = {
        items: this.state.items,
        deliveryLocation: this.state.deliveryLocation,
        promoCode: this.state.promoCode,
        promoDiscount: this.state.promoDiscount,
        timestamp: Date.now(),
      };
      localStorage.setItem(`kingrecharge:cart:${userId}`, JSON.stringify(cartData));
    } catch (error) {
      console.error('Erreur sauvegarde panier:', error);
    }
  }

  /**
   * Charge le panier depuis localStorage
   */
  loadCart() {
    try {
      const userId = this.getUserId();
      const stored = localStorage.getItem(`kingrecharge:cart:${userId}`);
      if (stored) {
        const cartData = JSON.parse(stored);
        this.state.items = cartData.items || [];
        this.state.deliveryLocation = cartData.deliveryLocation || null;
        this.state.promoCode = cartData.promoCode || null;
        this.state.promoDiscount = cartData.promoDiscount || 0;
        this.calculateTotals();
      }
    } catch (error) {
      console.error('Erreur chargement panier:', error);
    }
  }

  /**
   * Récupère l'ID utilisateur
   */
  getUserId() {
    // TODO: Récupérer depuis le user store via le proxy quand appelé depuis un composant
    // Pour l'instant, on utilise localStorage
    try {
      const userData = localStorage.getItem('kingrecharge:user');
      if (userData) {
        const user = JSON.parse(userData);
        return user?.id || 'guest';
      }
    } catch (error) {
      console.error('Erreur récupération utilisateur:', error);
    }
    return 'guest';
  }

  /**
   * Initialiser le store
   */
  init() {
    this.loadCart();
  }
}

export default CartStore;
